import { type IHeaderParams } from "ag-grid-enterprise";
import { useEffect, useState } from "react";

import { cn } from "@btnet/utilities-core";

import {
    BTIconCaretSmallDown,
    BTIconCaretSmallUp,
    BTIconPushpinFilled,
} from "commonComponents/btWrappers/BTIcon";
import { ButtonEmbedded } from "commonComponents/utilities/ButtonEmbedded/ButtonEmbedded";

import "./BillAgHeader.less";

/**
 * Custom AG Grid column header for the Bills list (ado-281381). Replaces AG Grid's default
 * three-dot column menu with the Kendo-parity interaction: clicking the header label toggles the
 * sort, and a dedicated pushpin button toggles the column's frozen (pinned-left) state. The menu
 * button is suppressed on the grid, so this is the only header affordance.
 */
export const BillAgHeader = (params: IHeaderParams) => {
    const { api, column, displayName, enableSorting, progressSort } = params;

    // AG Grid mutates sort/pin outside React, so subscribe to its events and mirror the state
    // locally to re-render the sort caret and pin highlight. Sort fires on the column; pin has no
    // per-column event here, so listen for the grid-level columnPinned event.
    const [sort, setSortState] = useState(column.getSort());
    const [isPinned, setIsPinned] = useState(column.getPinned() != null);

    useEffect(() => {
        const onSortChanged = () => setSortState(column.getSort());
        const onColumnPinned = () => setIsPinned(column.getPinned() != null);
        column.addEventListener("sortChanged", onSortChanged);
        api.addEventListener("columnPinned", onColumnPinned);
        return () => {
            // On teardown (route change, flag toggle, remount) AG Grid's destroy sequence can race
            // React's cleanup; removing listeners after the grid is destroyed throws. Guard it.
            if (api.isDestroyed()) {
                return;
            }
            column.removeEventListener("sortChanged", onSortChanged);
            api.removeEventListener("columnPinned", onColumnPinned);
        };
    }, [api, column]);

    const handleLabelClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        if (!enableSorting) {
            return;
        }
        // progressSort cycles asc → desc → none; suppressMultiSort on the grid keeps it single-column.
        progressSort(event.shiftKey);
    };

    const handlePinToggle = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        api.setColumnsPinned([column.getColId()], isPinned ? null : "left");
    };

    // Expose sort state to assistive tech. This custom header replaces AG Grid's default (which
    // ships its own a11y), and the sort direction is otherwise conveyed only by the visual caret.
    // AG Grid owns aria-sort on the outer .ag-header-cell (role="columnheader"); we carry the
    // direction in the label button's accessible name so a screen reader announces which way it's
    // sorted when focusing the control itself.
    const sortStateLabel =
        sort === "asc" ? ", sorted ascending" : sort === "desc" ? ", sorted descending" : "";

    return (
        <div className="BillAgHeader">
            <ButtonEmbedded
                className={cn("BillAgHeader-label", { "is-sortable": enableSorting })}
                onActionPerformed={handleLabelClick}
                aria-label={enableSorting ? `Sort by ${displayName}${sortStateLabel}` : undefined}
                data-testid={`${column.getColId()}-header`}
            >
                <span className="BillAgHeader-title">{displayName}</span>
                {sort === "asc" && <BTIconCaretSmallUp />}
                {sort === "desc" && <BTIconCaretSmallDown />}
            </ButtonEmbedded>
            <ButtonEmbedded
                className={cn("BillAgHeader-pushpin", { frozen: isPinned })}
                onActionPerformed={handlePinToggle}
                aria-label={`${isPinned ? "Unfreeze" : "Freeze"} ${displayName} column`}
                aria-pressed={isPinned}
                data-testid={`${column.getColId()}-columnFreeze`}
            >
                <BTIconPushpinFilled />
            </ButtonEmbedded>
        </div>
    );
};
